import {
  type Qualification,
  type WorkerShoppableQualification,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined, toError } from "@clipboard-health/util-ts";
import { HorizontalScrollView } from "@src/appV2/redesign/system/ui-components";
import { isAfter } from "date-fns";
import pluralize from "pluralize";
import { useState } from "react";

import { LoadingButton } from "../../components/LoadingButton";
import { PageFooterWrapper } from "../../components/PageFooterWrapper";

interface AddRolesFooterProps {
  addedQualificationIds: string[];
  qualifications: Qualification[];
  shoppableQualifications: WorkerShoppableQualification[];
  isVisible: boolean;
  isLoadingContinueButton?: boolean;
  canContinue: boolean;
  onDeleteRole: (shoppableQualificationId: string, qualificationId: string) => Promise<void>;
  onDeleteRoleError: (qualificationId: string, error: Error) => void;
  onDone: () => void;
}

interface SelectedQualificationData {
  qualificationId: string;
  shoppableQualification: WorkerShoppableQualification;
  qualification: Qualification;
}

function isSelectionQualificationData(item: {
  qualificationId: string;
  shoppableQualification: WorkerShoppableQualification | undefined;
  qualification: Qualification | undefined;
}): item is SelectedQualificationData {
  return isDefined(item.qualification) && isDefined(item.shoppableQualification);
}

function sortByRecentlyAddedFirst(a: SelectedQualificationData, b: SelectedQualificationData) {
  return isAfter(
    new Date(a.shoppableQualification.attributes.createdAt),
    new Date(b.shoppableQualification.attributes.createdAt)
  )
    ? -1
    : 1;
}

export function AddRolesFooter(props: AddRolesFooterProps) {
  const {
    addedQualificationIds,
    qualifications,
    shoppableQualifications,
    isVisible,
    isLoadingContinueButton = false,
    canContinue,
    onDeleteRole,
    onDeleteRoleError,
    onDone,
  } = props;

  const [deletingShoppableQualificationIds, setDeletingShoppableQualificationIds] = useState<
    string[]
  >([]);

  return (
    <PageFooterWrapper withShadow sx={{ display: isVisible ? "none" : "block" }}>
      {addedQualificationIds.length > 0 && (
        <HorizontalScrollView sx={{ marginBottom: 5 }} outerPaddingCompensation={5}>
          {addedQualificationIds
            .map((qualificationId) => ({
              qualificationId,
              shoppableQualification: shoppableQualifications.find(
                (shoppableQualification) =>
                  shoppableQualification.relationships.qualification.data.id === qualificationId
              ),
              qualification: qualifications.find(
                (qualification) => qualification.id === qualificationId
              ),
            }))
            .filter(isSelectionQualificationData)
            .sort(sortByRecentlyAddedFirst)
            .map(({ qualificationId, shoppableQualification, qualification }) => {
              return (
                <LoadingButton
                  key={qualificationId}
                  variant="outlined"
                  endIconType="remove"
                  isLoading={deletingShoppableQualificationIds.includes(shoppableQualification.id)}
                  onClick={async () => {
                    try {
                      setDeletingShoppableQualificationIds((previous) => [
                        ...previous,
                        shoppableQualification.id,
                      ]);

                      await onDeleteRole(shoppableQualification.id, qualificationId);
                    } catch (maybeError) {
                      const error = toError(maybeError);

                      onDeleteRoleError(qualificationId, error);
                    } finally {
                      setDeletingShoppableQualificationIds((previous) =>
                        previous.filter((id) => id !== shoppableQualification.id)
                      );
                    }
                  }}
                >
                  {qualification.attributes.displayLabel}
                </LoadingButton>
              );
            })}
        </HorizontalScrollView>
      )}

      <LoadingButton
        fullWidth
        variant="brand"
        disabled={!canContinue}
        size="large"
        isLoading={isLoadingContinueButton}
        onClick={async () => {
          onDone();
        }}
      >
        {addedQualificationIds.length > 0
          ? `Add ${addedQualificationIds.length} ${pluralize("role", addedQualificationIds.length)}`
          : "Add at least one role"}
      </LoadingButton>
    </PageFooterWrapper>
  );
}
